import React from 'react';
import { useApp } from '../context/AppContext';

/**
 * Header — sticky top bar with:
 *  - Back button (when on menu view)
 *  - Language toggle (EN / FR / AR)
 *  - Dark/Light mode toggle
 */

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'عربي' },
];

export default function Header({ restaurant, view, onBack }) {
  const { language, setLanguage, theme, toggleTheme, loc } = useApp();

  return (
    <header style={styles.header}>
      <div style={styles.left}>
        {view === 'menu' && (
          <button onClick={onBack} style={styles.backBtn} aria-label="Go back">
            {language === 'ar' ? '→' : '←'}
          </button>
        )}
      </div>

      <div style={styles.center}>
        {view === 'menu' && (
          <span style={styles.restaurantName}>{loc(restaurant?.name)}</span>
        )}
      </div>

      <div style={styles.right}>
        {/* Language switcher */}
        <div style={styles.langGroup}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              style={{
                ...styles.langBtn,
                ...(language === lang.code ? styles.langBtnActive : {}),
              }}
              aria-label={`Switch to ${lang.label}`}
            >
              {lang.label}
            </button>
          ))}
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          style={styles.themeBtn}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
      </div>
    </header>
  );
}

const styles = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 1rem',
    backgroundColor: 'var(--bg-glass)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid var(--border-light)',
    animation: 'slideDown 0.3s ease',
  },
  left: {
    flex: '0 0 40px',
    display: 'flex',
    alignItems: 'center',
  },
  center: {
    flex: 1,
    textAlign: 'center',
  },
  restaurantName: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  backBtn: {
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--bg-tertiary)',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    transition: 'all var(--transition-fast)',
  },
  langGroup: {
    display: 'flex',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: 'var(--radius-full)',
    padding: '2px',
    gap: '1px',
  },
  langBtn: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    borderRadius: 'var(--radius-full)',
    color: 'var(--text-muted)',
    transition: 'all var(--transition-fast)',
    letterSpacing: '0.02em',
  },
  langBtnActive: {
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    fontWeight: 600,
  },
  themeBtn: {
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--bg-tertiary)',
    fontSize: '0.95rem',
    transition: 'all var(--transition-fast)',
  },
};
